import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './Memo.css'; // Import CSS but it will only affect the outer container
import memoContent from '../assets/memoContent'; // Import original memo content
import memoUpdateContent from '../assets/memoUpdateContent'; // Import market traction update

function Memo() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Shadow DOM refs
  const shadowHostRef = useRef(null);
  const tocCleanupRef = useRef([]);
  const initializationAttempted = useRef(false);
  
  // State to track loading and error status
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Determine which version to show based on URL
  const isUpdateVersion = location.pathname.includes('/update');
  const currentHTML = isUpdateVersion ? memoUpdateContent : memoContent;
  
  // Extract styles from HTML
  function extractStyles(html) {
    try {
      const styleRegex = /<style>([\s\S]*?)<\/style>/i;
      const match = styleRegex.exec(html);
      return match && match[1] ? match[1].trim() : '';
    } catch (err) {
      console.error('Error extracting styles:', err);
      return '';
    }
  }

  // Extract body content from HTML
  function extractBodyContent(html) {
    try {
      const bodyRegex = /<body>([\s\S]*?)<\/body>/i;
      const match = bodyRegex.exec(html);
      
      if (match && match[1]) {
        return match[1].replace(/<script[\s\S]*?<\/script>/gi, '').trim();
      }
      return '';
    } catch (err) {
      console.error('Error extracting body content:', err);
      return '';
    }
  }
  
  // Cleanup function to avoid memory leaks
  const cleanupShadowDOM = useCallback(() => {
    console.log('Running shadow DOM cleanup...');
    try {
      // Run TOC cleanup functions
      if (tocCleanupRef.current.length > 0) {
        console.log('Cleaning up TOC event listeners...');
        tocCleanupRef.current.forEach(cleanup => {
          if (typeof cleanup === 'function') {
            try {
              cleanup();
            } catch (err) {
              console.error('Error in cleanup function:', err);
            }
          }
        });
        tocCleanupRef.current = [];
      }
      
      // Clean shadow root
      const shadowHost = shadowHostRef.current;
      if (shadowHost && shadowHost.shadowRoot) {
        console.log('Clearing shadow root content');
        shadowHost.shadowRoot.innerHTML = '';
      }
    } catch (err) {
      console.error('Error during cleanup:', err);
    }
  }, []);
  
  // Initialize Shadow DOM function
  const initializeShadowDOM = useCallback(() => {
    try {
      console.log('Starting Shadow DOM initialization...');
      const shadowHost = shadowHostRef.current;
      
      // Final check before proceeding
      if (!shadowHost) {
        throw new Error('Shadow host not available during initialization');
      }
      
      // Create shadow root if needed
      let shadowRoot;
      if (shadowHost.shadowRoot) {
        console.log('Using existing shadow root');
        shadowRoot = shadowHost.shadowRoot;
        shadowRoot.innerHTML = ''; // Clear any existing content
      } else {
        console.log('Creating new shadow root');
        shadowRoot = shadowHost.attachShadow({ mode: 'open' });
      }
      
      if (!currentHTML) {
        throw new Error('Memo HTML content is not available');
      }
      
      // Extract content from HTML
      console.log('Processing HTML content...');
      const styleContent = extractStyles(currentHTML);
      const bodyContent = extractBodyContent(currentHTML);
      
      // Create CSS for shadow DOM with additional responsive fixes
      const resetCSS = `
/* Reset styles for ShadowDOM content */
:host {
  all: initial;
  display: block;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  contain: content;
}

#memo-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.6;
  color: #333;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  width: 100%;
  overflow-x: hidden;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Grid layout fixes */
.page-container {
  display: grid !important;
  grid-template-columns: minmax(300px, 1fr) minmax(800px, 1000px) 1fr !important;
  min-height: 100vh !important;
  position: relative !important;
  gap: 2rem !important;
  width: 100% !important;
  max-width: 100% !important;
}

/* Critical TOC positioning styles */
.left-column {
  grid-column: 1 !important;
  position: relative !important;
  height: 100% !important;
  min-height: 100vh !important;
  display: block !important;
}

.toc-wrapper {
  position: sticky !important;
  top: 2rem !important;
  padding: 0 1rem !important;
  height: fit-content !important;
  display: flex !important;
  justify-content: flex-end !important;
  margin-top: 2rem !important;
  width: auto !important;
}

.toc-container {
  background-color: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 6px !important;
  padding: 1.2rem !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
  width: 250px !important;
  height: fit-content !important;
}

/* TOC content styles */
.toc-content {
  height: fit-content !important;
  overflow: hidden !important;
  transition: all 0.3s ease !important;
}

.toc-content.collapsed {
  height: 0 !important;
  opacity: 0 !important;
}

.toc-list {
  list-style-type: none !important;
  padding: 0 !important;
  margin: 0.75rem 0 0 !important;
}

/* Add styles for nested TOC items */
.toc-list .toc-list {
  margin-left: 1.5rem !important;
  margin-top: 0.5rem !important;
}

.toc-list .toc-list li a:before {
  width: 4px !important;
  height: 4px !important;
  background-color: #6b7280 !important;
}

.toc-list li {
  margin-bottom: 0.75rem !important;
  font-weight: 500 !important;
  font-size: 0.9rem !important;
  line-height: 1.2 !important;
}

.toc-list li:last-child {
  margin-bottom: 0 !important;
}

.toc-list li a {
  color: #1a1a1a !important;
  text-decoration: none !important;
  transition: color 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  padding: 0.25rem 0 !important;
}

.toc-list li a:hover {
  color: #2563eb !important;
}

/* Active TOC item styles */
.toc-list li a.active {
  color: #2563eb !important;
  font-weight: 600 !important;
}

.toc-list li a.active:before {
  transform: scale(1.5) !important;
  background-color: #2563eb !important;
}

/* Fix for TOC bullet points */
.toc-list li a:before {
  content: "" !important;
  display: inline-block !important;
  width: 6px !important;
  height: 6px !important;
  min-width: 6px !important;
  min-height: 6px !important;
  max-width: 6px !important;
  max-height: 6px !important;
  margin-right: 8px !important;
  background-color: #2563eb !important;
  border-radius: 50% !important;
  flex-shrink: 0 !important;
  transition: transform 0.2s ease, background-color 0.2s ease !important;
}

/* Main content column */
.main-column {
  grid-column: 2 !important;
  padding: 2rem 3rem !important;
  background-color: #ffffff !important;
  min-height: 100vh !important;
  width: 100% !important;
  max-width: 1000px !important;
  margin: 0 auto !important;
}

/* Right column */
.right-column {
  grid-column: 3 !important;
  display: block !important;
}

/* Mobile TOC adjustments */
.mobile-toc {
  display: none !important;
}

.mobile-toc .toc-container {
  width: 100% !important;
  max-width: 100% !important;
}

/* Mobile adjustments */
@media (max-width: 1024px) {
  .page-container {
    display: block !important;
    grid-template-columns: 1fr !important;
  }
  
  .left-column {
    display: none !important;
  }
  
  .right-column {
    display: none !important;
  }
  
  .main-column {
    grid-column: 1 !important;
    max-width: 100% !important;
    width: 100% !important;
    padding: 1rem !important;
  }
  
  .mobile-toc {
    display: block !important;
    margin-bottom: 2rem !important;
    position: relative !important;
    top: 0 !important;
    z-index: 100 !important;
    background-color: white !important;
    padding: 1rem !important;
    border-bottom: 1px solid #e5e7eb !important;
    margin: -1rem -1rem 1rem -1rem !important;
  }
  
  .mobile-toc .toc-container {
    width: 100% !important;
    max-height: calc(100vh - 4rem) !important;
    overflow-y: auto !important;
  }
}

/* Additional responsive fixes */
.statistics {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin: 2rem 0;
  width: 100%;
  box-sizing: border-box;
}

.stat-box {
  flex: 1;
  min-width: 200px;
  box-sizing: border-box;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

/* Mobile fixes */
@media (max-width: 768px) {
  .statistics {
    flex-direction: column;
    width: 100%;
    padding: 0;
    margin: 1.5rem 0;
    box-sizing: border-box;
  }
  
  .stat-box {
    width: 100%;
    min-width: 100%;
    margin-bottom: 1rem;
    box-sizing: border-box;
  }
  
  .main-column {
    padding: 1rem 1.25rem !important;
    overflow-x: hidden;
    box-sizing: border-box;
  }
  
  .page-container {
    overflow-x: hidden;
    width: 100%;
    box-sizing: border-box;
  }
  
  /* Fix other potential overflow issues */
  p, h1, h2, h3, h4, ul, ol, .quote {
    max-width: 100%;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  
  /* Fix team section on mobile */
  .team-section {
    flex-direction: column;
  }
  
  .team-member {
    width: 100%;
  }
}

/* Added media query for very small screens to adjust title */
@media (max-width: 480px) {
  #memo-container .main-title {
    font-size: 2.8rem; /* Slightly smaller font size */
    letter-spacing: -0.02em; 
  }
}

/* Mobile styles */
@media screen and (max-width: 768px) {
  .left-column {
    position: static;
    height: auto;
    padding: 1rem;
  }

  .toc-wrapper {
    position: static;
    width: 100%;
    height: auto;
    max-height: none;
    padding: 0;
    margin: 0;
  }

  .main-column {
    padding: 1rem;
  }

  .container {
    grid-template-columns: 1fr !important;
  }
}
`;
      
      // Build the DOM inside shadow root
      console.log('Building shadow DOM content...');
      
      // 1. Add styles
      const styleElement = document.createElement('style');
      styleElement.textContent = resetCSS + styleContent;
      shadowRoot.appendChild(styleElement);
      
      // 2. Add content container
      const contentContainer = document.createElement('div');
      contentContainer.id = 'memo-container';
      contentContainer.className = 'memo-page';
      contentContainer.innerHTML = bodyContent;
      shadowRoot.appendChild(contentContainer);
      
      // Give browser a moment to render the initial DOM structure
      setTimeout(() => {
        console.log('Running post-render initialization steps...');
        
        // 3. Fix paths and initialize functionality
        fixImagePaths(shadowRoot);
        
        // 4. Initialize TOC toggle with explicit checks
        console.log('Initializing TOC functionality...');
        tocCleanupRef.current = initializeTOCToggle(shadowRoot);
        
        // 5. Apply additional responsive fixes
        applyResponsiveFixesForMobile(shadowRoot);
        
        // 6. Apply security measures to prevent copying and inspection
        applyCopyProtection(shadowRoot);
        
        // 7. Fix TOC bullet points
        fixTOCBulletPoints(shadowRoot);
        
        // 8. Add TOC link click handlers
        setupTOCLinkHandlers(shadowRoot);
        
        // 9. After setting up TOC link handlers, add scroll spy
        const scrollSpyCleanup = setupScrollSpy(shadowRoot);
        
        // Add scroll spy cleanup to the cleanup functions
        tocCleanupRef.current.push(scrollSpyCleanup);
        
        // Final validation of TOC functionality
        setTimeout(() => {
          const tocWrapper = shadowRoot.querySelector('.toc-wrapper');
          const tocToggle = shadowRoot.querySelector('#tocToggle');
          
          console.log('Final TOC validation:');
          console.log('- TOC wrapper position:', tocWrapper ? getComputedStyle(tocWrapper).position : 'not found');
          console.log('- TOC wrapper top value:', tocWrapper ? getComputedStyle(tocWrapper).top : 'not found');
          console.log('- TOC toggle initialized:', !!tocToggle);
        }, 200);
        
        console.log('Shadow DOM initialization complete!');
        setLoading(false);
      }, 100);
    } catch (err) {
      console.error('Error initializing shadow DOM:', err);
      setError(`Error initializing memo content: ${err.message}`);
      setLoading(false);
    }
  }, []);
  
  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Add right-click prevention
  useEffect(() => {
    // Prevent context menu (right-click)
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Prevent keyboard shortcuts and inspect element
    const handleKeyDown = (e) => {
      // Prevent F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      if (
        e.key === 'F12' || 
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j')) ||
        (e.ctrlKey && (e.key === 'U' || e.key === 'u'))
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable selection on the memo page
    const handleSelectStart = (e) => {
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('selectstart', handleSelectStart);
    
    // Additional protection for the shadow DOM when it's created
    const addShadowDomProtection = () => {
      if (shadowHostRef.current?.shadowRoot) {
        const root = shadowHostRef.current.shadowRoot;
        const container = root.querySelector('#memo-container');
        
        if (container) {
          container.addEventListener('contextmenu', handleContextMenu);
          container.addEventListener('selectstart', handleSelectStart);
        }
      }
    };
    
    // Check periodically for shadow DOM availability
    const protectionInterval = setInterval(addShadowDomProtection, 500);
    
    // Capture the current value of shadowHostRef for cleanup
    const currentShadowHost = shadowHostRef.current;
    
    // Cleanup when component unmounts
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('selectstart', handleSelectStart);
      clearInterval(protectionInterval);
      
      // Clean up shadow DOM event listeners if they exist
      if (currentShadowHost?.shadowRoot) {
        const root = currentShadowHost.shadowRoot;
        const container = root.querySelector('#memo-container');
        
        if (container) {
          container.removeEventListener('contextmenu', handleContextMenu);
          container.removeEventListener('selectstart', handleSelectStart);
        }
      }
    };
  }, []);

  // Handle mounting and initialization
  useEffect(() => {
    console.log('Memo component mounted');
    
    // Use a longer delay to ensure the DOM is really ready
    const timer = setTimeout(() => {
      console.log('Attempting to initialize Shadow DOM after delay');
      if (!initializationAttempted.current) {
        initializationAttempted.current = true;
        
        if (shadowHostRef.current) {
          console.log('Shadow host found, initializing');
          initializeShadowDOM();
        } else {
          console.error('Shadow host STILL not available after delay');
          setError('Could not initialize content - please try refreshing the page');
          setLoading(false);
        }
      }
    }, 500); // Use a longer 500ms delay
    
    // Cleanup on unmount
    return () => {
      console.log('Memo component unmounting, cleaning up');
      clearTimeout(timer);
      cleanupShadowDOM();
    };
  }, [initializeShadowDOM, cleanupShadowDOM]); // Include dependencies

  // Body class management
  useEffect(() => {
    document.body.classList.add('memo-body');
    
    // Apply global body styles for copy protection
    document.body.style.userSelect = 'none';
    document.body.style.WebkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    document.body.style.MozUserSelect = 'none';
    
    return () => {
      document.body.classList.remove('memo-body');
      // Reset body styles
      document.body.style.userSelect = '';
      document.body.style.WebkitUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.MozUserSelect = '';
    };
  }, []);

  // Helper function to fix image paths in the shadow DOM
  function fixImagePaths(root) {
    try {
      const imagePaths = root.querySelectorAll('[style*="background-image"]');
      
      imagePaths.forEach(element => {
        const style = element.getAttribute('style');
        if (style && style.includes('/images/')) {
          const fixedStyle = style.replace(/\/images\//g, `${window.location.origin}/images/`);
          element.setAttribute('style', fixedStyle);
        }
      });
    } catch (err) {
      console.error('Error fixing image paths:', err);
    }
  }

  // Helper function to initialize TOC toggle functionality
  function initializeTOCToggle(root) {
    const cleanupFunctions = [];
    
    try {
      console.log('Initializing TOC toggle functionality...');
      
      // Log the presence of key elements
      const leftColumn = root.querySelector('.left-column');
      const tocWrapper = root.querySelector('.toc-wrapper');
      console.log('Left column exists:', !!leftColumn);
      console.log('TOC wrapper exists:', !!tocWrapper);
      
      // Add additional CSS to fix TOC positioning if needed
      const additionalStyle = document.createElement('style');
      additionalStyle.textContent = `
        /* Extra TOC fixes for Shadow DOM */
        .left-column {
          position: relative !important;
          grid-column: 1 !important;
          min-height: 100vh !important;
        }
        
        .toc-wrapper {
          position: sticky !important;
          top: 2rem !important;
          display: flex !important;
          justify-content: flex-end !important;
          height: fit-content !important;
          padding: 0 1rem !important;
        }
        
        /* Improve TOC layout on smaller but still desktop screens */
        @media (min-width: 1025px) and (max-width: 1200px) {
          .toc-container {
            width: 220px !important;
          }
        }
        
        /* Mobile TOC styles */
        @media (max-width: 1024px) {
          .left-column {
            display: none !important;
          }
          
          .mobile-toc {
            display: block !important;
          }
        }
      `;
      root.appendChild(additionalStyle);
      
      // Desktop TOC toggle
      const tocToggle = root.querySelector('#tocToggle');
      const tocContent = root.querySelector('#tocContent');
      
      if (tocToggle && tocContent) {
        console.log('Found desktop TOC elements, initializing toggle');
        
        // Set initial state (expanded for desktop)
        let isCollapsed = false;
        
        // Make sure it's shown initially
        tocContent.classList.remove('collapsed');
        tocToggle.classList.remove('collapsed');
        
        const handleTocToggleClick = function() {
          isCollapsed = !isCollapsed;
          
          if (isCollapsed) {
            tocToggle.classList.add('collapsed');
            tocContent.classList.add('collapsed');
          } else {
            tocToggle.classList.remove('collapsed');
            tocContent.classList.remove('collapsed');
          }
        };
        
        tocToggle.addEventListener('click', handleTocToggleClick);
        cleanupFunctions.push(() => tocToggle.removeEventListener('click', handleTocToggleClick));
      } else {
        console.warn('TOC elements not found in the shadow DOM');
      }
      
      // Mobile TOC toggle
      const mobileTocToggle = root.querySelector('#mobileTocToggle');
      const mobileTocContent = root.querySelector('#mobileTocContent');
      
      if (mobileTocToggle && mobileTocContent) {
        // Mobile TOC is collapsed by default
        mobileTocToggle.classList.add('collapsed');
        mobileTocContent.classList.add('collapsed');
        
        const handleMobileTocToggleClick = function() {
          if (mobileTocToggle.classList.contains('collapsed')) {
            mobileTocToggle.classList.remove('collapsed');
            mobileTocContent.classList.remove('collapsed');
          } else {
            mobileTocToggle.classList.add('collapsed');
            mobileTocContent.classList.add('collapsed');
          }
        };
        
        mobileTocToggle.addEventListener('click', handleMobileTocToggleClick);
        cleanupFunctions.push(() => mobileTocToggle.removeEventListener('click', handleMobileTocToggleClick));
      }
    } catch (err) {
      console.error('Error initializing TOC toggles:', err);
    }
    
    return cleanupFunctions;
  }

  // Setup TOC link handlers to enable navigation within shadow DOM
  const setupTOCLinkHandlers = (root) => {
    try {
      // Get all TOC links in both desktop and mobile TOCs
      const tocLinks = root.querySelectorAll('.toc-list li a');
      
      console.log('Setting up TOC link handlers for', tocLinks.length, 'links');
      
      tocLinks.forEach(link => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          
          // Extract the section ID from the href attribute
          const href = link.getAttribute('href');
          const sectionId = href.replace('#', '');
          
          console.log('TOC link clicked for section:', sectionId);
          
          // Find the target section in the shadow DOM
          const targetSection = root.getElementById(sectionId);
          
          if (targetSection) {
            console.log('Found target section, scrolling to it');
            // Smooth scroll to the section
            targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            
            // Close mobile TOC if it's open
            const mobileTocToggle = root.querySelector('#mobileTocToggle');
            const mobileTocContent = root.querySelector('#mobileTocContent');
            
            if (mobileTocToggle && mobileTocContent && !mobileTocToggle.classList.contains('collapsed')) {
              mobileTocToggle.classList.add('collapsed');
              mobileTocContent.classList.add('collapsed');
            }
          } else {
            console.error('Target section not found:', sectionId);
          }
        });
      });
      
      // Also handle regular navigation links outside the TOC
      const allLinks = root.querySelectorAll('a[href^="#"]');
      allLinks.forEach(link => {
        if (!link.closest('.toc-list')) {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            
            const href = link.getAttribute('href');
            const sectionId = href.replace('#', '');
            
            console.log('Regular link clicked for section:', sectionId);
            
            const targetSection = root.getElementById(sectionId);
            if (targetSection) {
              targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          });
        }
      });
      
    } catch (err) {
      console.error('Error setting up TOC link handlers:', err);
    }
  };

  // Apply protection against copying content
  const applyCopyProtection = (root) => {
    try {
      // Prevent right-click in shadow DOM
      const container = root.querySelector('#memo-container');
      if (container) {
        container.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          return false;
        });
        
        // Prevent selection in shadow DOM
        container.addEventListener('selectstart', (e) => {
          e.preventDefault();
          return false;
        });
        
        // Disable drag in shadow DOM
        container.addEventListener('dragstart', (e) => {
          e.preventDefault();
          return false;
        });
        
        // Add CSS to the shadow DOM to prevent selection
        const style = document.createElement('style');
        style.textContent = `
          * {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }
          
          /* Disable image dragging */
          img {
            -webkit-user-drag: none;
            -khtml-user-drag: none;
            -moz-user-drag: none;
            -o-user-drag: none;
            user-drag: none;
          }
        `;
        root.appendChild(style);
      }
    } catch (err) {
      console.error('Error applying copy protection:', err);
    }
  };

  // Apply additional responsive fixes for mobile devices
  const applyResponsiveFixesForMobile = (root) => {
    try {
      // Fix statistics section
      const statistics = root.querySelector('.statistics');
      if (statistics) {
        console.log('Applying fixes to statistics section');
        
        // Ensure statistics container has proper constraints
        statistics.style.maxWidth = '100%';
        statistics.style.overflowX = 'hidden';
        statistics.style.boxSizing = 'border-box';
        statistics.style.paddingRight = '10px'; // Add right padding
        
        // Fix individual stat boxes
        const statBoxes = statistics.querySelectorAll('.stat-box');
        statBoxes.forEach(box => {
          // Ensure each box can shrink properly
          box.style.minWidth = '0';
          box.style.boxSizing = 'border-box';
          box.style.width = 'calc(100% - 10px)'; // Ensure width with margin
        });
      }
      
      // Ensure main column doesn't overflow
      const mainColumn = root.querySelector('.main-column');
      if (mainColumn) {
        mainColumn.style.overflowX = 'hidden';
        mainColumn.style.maxWidth = '100%';
        mainColumn.style.boxSizing = 'border-box';
        
        // Increase padding on mobile
        if (window.innerWidth <= 768) {
          mainColumn.style.padding = '1rem 1.25rem';
        }
      }
      
      // Fix page container
      const pageContainer = root.querySelector('.page-container');
      if (pageContainer) {
        pageContainer.style.overflowX = 'hidden';
        pageContainer.style.maxWidth = '100%';
        pageContainer.style.boxSizing = 'border-box';
      }
      
      // Adjust content width on mobile
      if (window.innerWidth <= 768) {
        const contentElements = root.querySelectorAll('p, h1, h2, h3, h4, ul, ol, .quote, .statistics, .stat-box');
        contentElements.forEach(el => {
          el.style.boxSizing = 'border-box';
          el.style.maxWidth = '100%';
          
          // Add some right margin to prevent touching edge
          if (el.classList.contains('stat-box')) {
            el.style.marginRight = '5px';
            el.style.width = 'calc(100% - 5px)';
          }
        });
      }
    } catch (err) {
      console.error('Error applying responsive fixes:', err);
    }
  };

  // Function to fix TOC bullet points
  const fixTOCBulletPoints = (root) => {
    try {
      // Fix desktop TOC bullets
      const tocListItems = root.querySelectorAll('.toc-list li a');
      console.log('Fixing TOC bullet points for', tocListItems.length, 'items');
      
      tocListItems.forEach(link => {
        // Check if there's an existing pseudo-element or replace it
        const existingBullet = link.querySelector('.toc-bullet');
        
        if (!existingBullet) {
          // Create a proper circle bullet
          const bullet = document.createElement('span');
          bullet.className = 'toc-bullet';
          bullet.style.display = 'inline-block';
          bullet.style.width = '6px';
          bullet.style.height = '6px';
          bullet.style.minWidth = '6px';
          bullet.style.minHeight = '6px';
          bullet.style.marginRight = '8px';
          bullet.style.backgroundColor = '#2563eb';
          bullet.style.borderRadius = '50%';
          bullet.style.flexShrink = '0';
          
          // Insert at beginning of link
          link.insertBefore(bullet, link.firstChild);
          
          // Ensure link has flex display
          link.style.display = 'flex';
          link.style.alignItems = 'center';
        }
      });
      
      // Also fix mobile TOC bullets
      const mobileTocListItems = root.querySelectorAll('#mobileTocContent .toc-list li a');
      mobileTocListItems.forEach(link => {
        const existingBullet = link.querySelector('.toc-bullet');
        
        if (!existingBullet) {
          const bullet = document.createElement('span');
          bullet.className = 'toc-bullet';
          bullet.style.display = 'inline-block';
          bullet.style.width = '6px';
          bullet.style.height = '6px';
          bullet.style.minWidth = '6px';
          bullet.style.minHeight = '6px';
          bullet.style.marginRight = '8px';
          bullet.style.backgroundColor = '#2563eb';
          bullet.style.borderRadius = '50%';
          bullet.style.flexShrink = '0';
          
          link.insertBefore(bullet, link.firstChild);
          link.style.display = 'flex';
          link.style.alignItems = 'center';
        }
      });
      
      // Add some additional CSS to ensure the bullets are always circular
      const additionalStyle = document.createElement('style');
      additionalStyle.textContent = `
        .toc-bullet {
          display: inline-block !important;
          width: 6px !important;
          height: 6px !important;
          min-width: 6px !important;
          min-height: 6px !important;
          margin-right: 8px !important;
          background-color: #2563eb !important;
          border-radius: 50% !important;
          flex-shrink: 0 !important;
        }
        
        .toc-list li a {
          display: flex !important;
          align-items: center !important;
        }
        
        /* Override any existing pseudo-elements */
        .toc-list li a:before {
          display: none !important;
        }
      `;
      root.appendChild(additionalStyle);
      
    } catch (err) {
      console.error('Error fixing TOC bullet points:', err);
    }
  };

  // Add scroll spy functionality
  const setupScrollSpy = (root) => {
    try {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            const id = entry.target.getAttribute('id');
            const tocLink = root.querySelector(`.toc-list a[href="#${id}"]`);
            
            if (tocLink) {
              if (entry.isIntersecting) {
                // Remove active class from all links
                root.querySelectorAll('.toc-list a').forEach(link => {
                  link.classList.remove('active');
                });
                // Add active class to current link
                tocLink.classList.add('active');
              }
            }
          });
        },
        {
          root: null,
          rootMargin: '-20% 0px -80% 0px', // Adjust these values to change when the active state triggers
          threshold: 0
        }
      );
      
      // Observe all section headings
      const headings = root.querySelectorAll('h1[id], h2[id], h3[id], h4[id]');
      headings.forEach(heading => observer.observe(heading));
      
      return () => {
        observer.disconnect();
      };
    } catch (err) {
      console.error('Error setting up scroll spy:', err);
      return () => {};
    }
  };

  return (
    <div 
      className="memo-wrapper"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Always render the shadow host, but hide it if we're still loading */}
      <div 
        ref={shadowHostRef} 
        className="shadow-host"
        style={{ 
          width: '100%', 
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: loading || error ? 'none' : 'block',
          overflowX: 'hidden', // Prevent horizontal scrolling
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
        onContextMenu={(e) => e.preventDefault()}
      />
      
      {/* Conditionally show loading or error state */}
      {loading && (
        <div className="memo-loading" style={{ 
          width: '100%', 
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: '2rem',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}>
          <div>
            <h2>Loading memo content...</h2>
            <p>Please wait while we load the content.</p>
          </div>
        </div>
      )}
      
      {error && (
        <div className="memo-error" style={{ 
          width: '100%', 
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: '2rem',
          color: '#e53e3e',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}>
          <div>
            <h2>Error Loading Memo</h2>
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer'
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Memo;